import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { AuthService } from '@merchant-portal/app/core/auth.service';
import { OtpStoreFactory } from '@merchant-portal/app/core/otp/factory/otp-store.factory';
import { OtpComponent } from '@merchant-portal/app/core/otp/otp.component';
import { OtpStore } from '@merchant-portal/app/core/otp/store/otp.store';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { InputTextModule } from 'primeng/inputtext';
import { PasswordModule } from 'primeng/password';
import { of } from 'rxjs';
import { catchError } from 'rxjs/operators';

@Component({
  selector: 'app-change-password-dialog',
  imports: [
    CommonModule,
    CardModule,
    InputTextModule,
    PasswordModule,
    ReactiveFormsModule,
    ButtonModule,
    OtpComponent,
  ],
  templateUrl: './verify-change-password-dialog.component.html',
  styleUrls: ['./verify-change-password-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [AuthService, { provide: OtpStoreFactory, useClass: OtpStore }],
})
export class VerifyChangePasswordDialogComponent {
  private authService = inject(AuthService);
  private otpStore = inject(OtpStoreFactory);

  private password = '';
  private newPassword = '';
  private otpToken = '';
  private email = '';
  private isProcessing = false; // Prevent multiple API calls
  twoFactorAuthMethod: 'OTP' | 'TOTP' | string | null = null; // Make this public

  // OTP component inputs
  countDownStarted = true;
  resendDelaySeconds = 30;

  constructor(
    private dialogConfig: DynamicDialogConfig,
    private ref: DynamicDialogRef
  ) {
    // Get data from the account settings service call
    this.password = this.dialogConfig.data?.password;
    this.newPassword = this.dialogConfig.data?.newPassword;
    this.otpToken = this.dialogConfig.data?.otpToken;
    this.email = this.dialogConfig.data?.email || this.authService.getEmail();
    this.resendDelaySeconds = this.dialogConfig.data?.resendDelaySeconds || 30;
    this.twoFactorAuthMethod = this.dialogConfig.data?.twoFactorAuthMethod || null;

    // Set email in auth service for OTP component to use
    if (this.email) {
      this.authService.setEmail(this.email);
    }

    // Initialize OTP store with the token and start countdown
    if (this.otpToken) {
      this.authService.setTempToken(this.otpToken);
      this.otpStore.setInitialSession(this.otpToken, this.resendDelaySeconds);
      this.otpStore.startCountdown();
    }
  }

  onOtpComplete(isComplete: boolean) {
    if (isComplete && !this.isProcessing) {
      this.isProcessing = true; // Prevent multiple calls

      this.authService
        .changePassword({
          newPassword: this.newPassword,
          password: this.password,
        })
        .pipe(
          catchError((error: HttpErrorResponse) => {
            return of(error);
          })
        )
        .subscribe((authRes: any) => {
          if (authRes && authRes.code === '00') {
            this.ref.close({ success: true });
          } else {
            this.ref.close({
              success: false,
              error: authRes?.error?.message || 'Password change failed',
            });
          }
        });
    }
  }
}
